import { useEffect } from 'react';
import { usePostHog } from '@posthog/react';
import { fetchQuery, useMutation, useRelayEnvironment } from 'react-relay';
import * as Sentry from '@sentry/react';
import { getExperimentFlagHistory, recordExperimentAssignment } from '../graphQLOperations/Experiments';
import type { ExperimentsRecordExperimentsAssignmentMutation } from '../graphQLOperations/__generated__/ExperimentsRecordExperimentsAssignmentMutation.graphql';
import type { ExperimentsGetExperimentsFlagHistoryQuery } from '../graphQLOperations/__generated__/ExperimentsGetExperimentsFlagHistoryQuery.graphql';

export const NO_CONDITION_VARIANT_NAME = 'disabled';

export type Assignment = { flag: string; variant: string };

type HistoryRow = { flagName?: string | null; variantName?: string | null; createdOn?: unknown } | null | undefined;

export const experimentVariants = (variants: Record<string, string | boolean>): Map<string, string> =>
  new Map(Object.entries(variants).filter((entry): entry is [string, string] => typeof entry[1] === 'string'));

export const latestVariantByFlag = (rows: readonly HistoryRow[]): Map<string, string> => {
  const latest = new Map<string, { variant: string; at: number }>();

  rows.forEach(row => {
    if (!row?.flagName || !row.variantName) return;
    const at = Date.parse(String(row.createdOn ?? '')) || 0;
    const existing = latest.get(row.flagName);
    if (!existing || at >= existing.at) latest.set(row.flagName, { variant: row.variantName, at });
  });

  return new Map(Array.from(latest, ([flag, { variant }]) => [flag, variant]));
};

export const flagTransitions = (recorded: Map<string, string>, current: Map<string, string>): Assignment[] => {
  const changes: Assignment[] = [];

  current.forEach((variant, flag) => {
    if (recorded.get(flag) !== variant) changes.push({ flag, variant });
  });

  recorded.forEach((variant, flag) => {
    if (variant === NO_CONDITION_VARIANT_NAME) return;
    if (!current.has(flag)) changes.push({ flag, variant: NO_CONDITION_VARIANT_NAME });
  });

  return changes;
};

export const useAssignmentLogger = (publicUserId: string | undefined): void => {
  const posthog = usePostHog();
  const environment = useRelayEnvironment();
  const [commitAssignment] = useMutation<ExperimentsRecordExperimentsAssignmentMutation>(recordExperimentAssignment);

  useEffect(() => {
    if (!publicUserId) return;

    const recordedVariants = fetchQuery<ExperimentsGetExperimentsFlagHistoryQuery>(
      environment,
      getExperimentFlagHistory,
      {
        userId: publicUserId,
      },
    )
      .toPromise()
      .then(data => latestVariantByFlag(data?.userByPublicUserId?.experimentsFlagHistories ?? []))
      .catch(error => {
        Sentry.captureException(error);
        return new Map<string, string>();
      });

    let active = true;

    const record = (recorded: Map<string, string>, { flag, variant }: Assignment) => {
      recorded.set(flag, variant);

      commitAssignment({
        variables: { input: { input: { flagName: flag, variantName: variant } } },
        onCompleted: response => {
          const errors = response.addExperimentsFlagAssignment?.errors;
          if (!errors?.length) return;
          Sentry.captureException(
            new Error(
              `Failed to record assignment ${flag}=${variant}: ${errors.map(error => error.message).join(', ')}`,
            ),
          );
        },
      });
    };

    const unsubscribe = posthog.onFeatureFlags((_flags, variants, context) => {
      const replayedBeforeSubscribe = context === undefined;
      const identified = posthog.get_distinct_id() === publicUserId;
      if (replayedBeforeSubscribe || !identified) return;

      const current = experimentVariants(variants);
      void recordedVariants.then(recorded => {
        if (!active) return;
        flagTransitions(recorded, current).forEach(change => record(recorded, change));
      });
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [posthog, environment, commitAssignment, publicUserId]);
};
